import React, { useEffect, useRef } from 'react';
import Markdown from './Markdown';

export const SUGGESTIONS = [
  'Aaj ki sales aur pending orders kitne hain?',
  'Kaun se products ka stock 5 ya us se kam hai?',
  'Is mahine ke top 5 selling products dikhao',
  'Last 7 din ki daily sales report',
];

const STATUS_STYLE = {
  preparing: 'bg-gray-50 text-gray-500 border-gray-200',
  running: 'bg-blue-50 text-blue-700 border-blue-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  error: 'bg-rose-50 text-rose-700 border-rose-200',
  declined: 'bg-gray-100 text-gray-500 border-gray-200 line-through',
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  stopped: 'bg-gray-50 text-gray-400 border-gray-200',
};
const STATUS_ICON = { preparing: '…', running: '⟳', done: '✓', error: '!', declined: '✕', pending: '?', stopped: '–' };

function ToolChip({ part }) {
  return (
    <div className={`inline-flex items-start gap-2 max-w-full text-xs border rounded-lg px-2.5 py-1.5 ${STATUS_STYLE[part.status] || STATUS_STYLE.done}`}>
      <span className={`font-bold ${part.status === 'running' || part.status === 'preparing' ? 'animate-pulse' : ''}`}>{STATUS_ICON[part.status] || '•'}</span>
      <span className="min-w-0">
        <span className="font-medium">{part.write ? part.summary || part.label : part.label}</span>
        {part.error && <span className="block mt-0.5 opacity-80">{part.error}</span>}
      </span>
    </div>
  );
}

function AssistantMessage({ msg, busy, compact }) {
  return (
    <div className={`flex ${compact ? 'gap-2' : 'gap-3'}`}>
      <div
        className={`${compact ? 'w-6 h-6 text-xs' : 'w-8 h-8 text-sm'} shrink-0 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold`}
      >
        C
      </div>
      <div className="min-w-0 flex-1 space-y-2 pt-0.5">
        {msg.parts.map((p, i) =>
          p.type === 'text' ? (
            <Markdown key={i} text={p.text} />
          ) : p.type === 'tool' ? (
            <div key={p.id || i}>
              <ToolChip part={p} />
            </div>
          ) : (
            <div key={i} className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{p.msg}</div>
          )
        )}
        {busy && (
          <div className="flex gap-1 py-1">
            {[0, 1, 2].map((d) => (
              <span key={d} className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: `${d * 150}ms` }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Messages, approval card and input for one chat (from useAssistantChat). */
export default function ChatBody({ chat, inactive, compact = false }) {
  const { messages, pending, busy, input, setInput, inputRef, send, answer } = chat;
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, pending]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const lastAssistantIdx = messages.map((m) => m.role).lastIndexOf('assistant');
  const suggestions = compact ? SUGGESTIONS.slice(0, 3) : SUGGESTIONS;

  return (
    <>
      <div className={`flex-1 overflow-y-auto ${compact ? 'p-3 space-y-4' : 'p-4 sm:p-6 space-y-5'}`}>
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center gap-4">
            <div className="w-12 h-12 rounded-full bg-orange-500 text-white flex items-center justify-center text-xl font-bold">C</div>
            <p className="text-gray-500 text-sm max-w-md">
              Main aap ke store ka data dekh kar jawab deta hoon. Kuch badalna ho (order status, coupon, stock) to pehle aap se approval lunga.
            </p>
            <div className="flex flex-wrap justify-center gap-2 max-w-xl">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  disabled={busy || inactive}
                  className="px-3 py-1.5 text-xs border border-gray-200 rounded-full text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) =>
          m.role === 'user' ? (
            <div key={i} className="flex justify-end">
              <div
                className={`max-w-[85%] whitespace-pre-wrap text-sm rounded-2xl rounded-br-md px-4 py-2.5 ${m.retracted ? 'bg-gray-100 text-gray-400 line-through' : 'bg-gray-900 text-white'}`}
                title={m.retracted ? 'Claude declined this message; it was not kept in the chat' : ''}
              >
                {m.text}
              </div>
            </div>
          ) : (
            <AssistantMessage key={i} msg={m} busy={busy && i === lastAssistantIdx} compact={compact} />
          )
        )}

        {pending && !busy && (
          <div className={`${compact ? 'ml-8' : 'ml-11'} border border-amber-200 bg-amber-50 rounded-2xl p-4 space-y-3`}>
            <p className="text-sm font-semibold text-amber-900">Claude wants to make {pending.length === 1 ? 'this change' : 'these changes'}:</p>
            <ul className="space-y-1">
              {pending.map((a) => (
                <li key={a.id} className="text-sm text-amber-900">
                  • {a.summary}
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <button onClick={() => answer(true)} className="px-4 py-2 text-sm font-medium rounded-xl bg-emerald-600 text-white hover:bg-emerald-700">
                Approve
              </button>
              <button onClick={() => answer(false)} className="px-4 py-2 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
                Reject
              </button>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className={`border-t border-gray-100 ${compact ? 'p-2' : 'p-3'}`}>
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={inactive}
            placeholder={
              pending
                ? 'Type to reject and tell Claude what to do instead…'
                : compact
                ? 'Message Claude…'
                : 'Message Claude…  (Enter to send, Shift+Enter for new line)'
            }
            className="flex-1 resize-none max-h-40 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50"
            style={{ minHeight: 44 }}
          />
          <button
            onClick={() => send()}
            disabled={busy || !input.trim() || inactive}
            className={`h-11 ${compact ? 'px-4' : 'px-5'} rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium disabled:opacity-50`}
          >
            {busy ? '…' : 'Send'}
          </button>
        </div>
        {!compact && <p className="mt-1.5 text-[11px] text-gray-400">Claude can make mistakes — check important numbers before acting on them.</p>}
      </div>
    </>
  );
}
